import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { getSeatResources } from './actions';
import type { ExternalSeatData, SeatData, RenderRules } from '../models/types';

// Создаем адаптеры для схем и мест
export const schemasAdapter = createEntityAdapter<ExternalSeatData>();

export const seatsAdapter = createEntityAdapter<SeatData>();

export interface SelectedSeat {
  seatId: number;
  seatName: string;
  sectorName: string;
  rowName: string;
  seatNumber: string;
  price: number;
  eventId: number;
  visibility?: string | null;
  previewImageUrl?: string | null;
}

export interface SeatSelectionState {
  schemas: ReturnType<typeof schemasAdapter.getInitialState>;
  seats: ReturnType<typeof seatsAdapter.getInitialState>;
  selectedSeats: SelectedSeat[];
  isSeatResourcesLoading: boolean;
  loadedSeatResourcesFor: { showId: number; eventId: number } | null;
  isLoading: boolean;
  error: string | null;
  urlParamsData: { showIds?: number; eventsIds?: number };
  schemaSvg: string | null;
  renderRules: RenderRules | null;
}

const initialState: SeatSelectionState = {
  schemas: schemasAdapter.getInitialState(),
  seats: seatsAdapter.getInitialState(),
  selectedSeats: [],
  isLoading: false,
  isSeatResourcesLoading: false,
  loadedSeatResourcesFor: null,
  error: null,
  urlParamsData: { showIds: undefined, eventsIds: undefined },
  renderRules: null,
  schemaSvg: null,
};

const seatSelectionSlice = createSlice({
  name: 'seatSelection',
  initialState,
  reducers: {
    toggleSeatSelection: (state, action: PayloadAction<SelectedSeat>) => {
      const existingIndex = state.selectedSeats.findIndex(
        seat => seat.seatId === action.payload.seatId
      );

      if (existingIndex >= 0) {
        state.selectedSeats.splice(existingIndex, 1);
      } else {
        state.selectedSeats.push(action.payload);
      }
    },
    clearSelectedSeats: state => {
      state.selectedSeats = [];
    },
    resetSeatSelection: () => initialState,
    setUrlParamsData: (state, action: PayloadAction<{ showIds?: string; eventsIds?: string }>) => {
      state.urlParamsData = {
        showIds: Number(action.payload.showIds) || undefined,
        eventsIds: Number(action.payload.eventsIds) || undefined,
      };
    },
  },
  extraReducers: builder => {
    builder

      // Load combined resources (SVG + seatmap + future render rules)
      .addCase(getSeatResources.pending, state => {
        state.isSeatResourcesLoading = true;

        state.error = null;
      })
      .addCase(getSeatResources.fulfilled, (state, action) => {
        state.isSeatResourcesLoading = false;
        const { showId, eventId } = action.meta.arg;
        state.loadedSeatResourcesFor = { showId, eventId };

        seatsAdapter.setAll(state.seats, action.payload.seats);
        state.schemaSvg = action.payload.schemaSvg;
        schemasAdapter.setAll(state.schemas, action.payload.seatmap);
        state.renderRules = action.payload.renderRules ?? null;
      })
      .addCase(getSeatResources.rejected, (state, action) => {
        state.isSeatResourcesLoading = false;

        state.error =
          (action.payload as string) || action.error.message || 'Не удалось загрузить данные';
      });
  },
});

export const { toggleSeatSelection, clearSelectedSeats, resetSeatSelection, setUrlParamsData } =
  seatSelectionSlice.actions;

export default seatSelectionSlice.reducer;
